import { configureStore } from '@reduxjs/toolkit';
import systemReducer from './slices/systemSlice';
import authReducer from './slices/authSlice';
import globalDataReducer from './slices/globalDataSlice';
import activeLocationReducer from './slices/activeLocationSlice';
import mapReducer from './slices/mapSlice';
import locationsReducer from './slices/locationsSlice';
import editorReducer from './slices/editorSlice';
import userSpecificReducer from './slices/userSpecificSlice';
import mapUiReducer from './slices/mapUiSlice';
import editorSettingsReducer from './slices/editorSettingsSlice';
import adminReducer from './slices/adminSlice';
import customMapsReducer from './slices/customMapsSlice';
import diaryFiltersReducer from './slices/diaryFiltersSlice';
import sodaSearchFiltersReducer from './slices/sodaSearchFiltersSlice';
import activeDrinkReducer from './slices/activeDrinkSlice';
// Import other reducers here as they are created
// import locationsReducer from './slices/locationsSlice';
// ... etc.

export const store = configureStore({
  reducer: {
    system: systemReducer,
    auth: authReducer,
    globalData: globalDataReducer,
    activeLocation: activeLocationReducer,
    map: mapReducer,
    mapUi: mapUiReducer,
    locations: locationsReducer,
    editor: editorReducer,
    userSpecific: userSpecificReducer,
    editorSettings: editorSettingsReducer,
    admin: adminReducer,
    customMaps: customMapsReducer,
    diaryFilters: diaryFiltersReducer,
    sodaSearchFilters: sodaSearchFiltersReducer,
    activeDrink: activeDrinkReducer,
    // Add other reducers here
    // locations: locationsReducer,
  },
  // Middleware can be added here if needed (e.g., for RTK Query, logging)
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({
    // Firebase Timestamps are not serializable, configure middleware to ignore
    serializableCheck: {
      ignoredActions: [
          'globalData/initialize/fulfilled', // Ignore for potential non-serializable listener functions if any
          'activeLocation/fetchData/fulfilled', // Ignore fulfillment as listeners might be involved
          'locations/fetchLocations/fulfilled', // Ignore fulfilled fetch action
          'locations/addLocation/fulfilled', // Ignore fulfilled add action (returns Location)
          'locations/updateLocation/fulfilled', // Ignore fulfilled update action
          'userSpecific/initializeData/fulfilled', // Ignore listener setup fulfillment
          'auth/checkAuthState/fulfilled', // Added to ignore the unsubscribe function in payload
          'admin/initializeListeners/fulfilled', // Ignore admin listeners
          // Potentially ignore adapter actions if Timestamps cause problems during updates
          'userSpecific/visitedLocationAdded',
          'userSpecific/visitedLocationUpdated',
          'userSpecific/reviewAdded',
          'userSpecific/reviewUpdated',
          // No need to ignore deleteLocation/fulfilled as it returns only string ID
      ],
      ignoredPaths: [
          'globalData.brands', // Ignore entire entity states if Timestamps are nested within
          'globalData.drinks',
          'globalData.flavors',
          'globalData.attributes',
          'activeLocation', // Ignore the whole activeLocation state due to potential non-serializable data (Timestamps in Location/Sighting)
          'locations.entities', // Ignore all entities within locations slice
          'userSpecific.visitedLocations.entities', // Ignore all visited location entities
          'userSpecific.reviews.entities', // Ignore all review entities
          'admin.unsubscribeFunctions', // Ignore unsubscribe functions in admin slice
          'customMaps.myMaps.entities', // Ignore custom map entities for potential Timestamps if not fully serialized
          'customMaps.publicMaps.entities', // Ignore custom map entities
          // Or more specific paths if needed: 'locations.entities.*.createdAt', 'locations.entities.*.updatedAt'
      ],
    },
  }),
  // DevTools extension is enabled by default
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: { system: SystemState, auth: AuthState, globalData: GlobalDataState, activeLocation: ActiveLocationState, map: MapState ... }
export type AppDispatch = typeof store.dispatch; 